import * as React from 'react'

import { DefinedBgColorProvider } from '~theme/BgColorProvider'
import { ThemeProviderRaw } from '~theme/ThemeProvider'
import { TStatusColorKey } from '~theme/typings'
import { ButtonIcon } from '~ui/components/buttons/ButtonIcon/ButtonIcon'
import { Card } from '~ui/components/content/Card/Card'
import { Icon } from '~ui/components/icons/Icon'
import { IconAlertFill } from '~ui/components/icons/specific/IconAlert'
import { IconCheckMarkCircleFill } from '~ui/components/icons/specific/IconCheckMark'
import { IconInfoFill } from '~ui/components/icons/specific/IconInfo'
import { IconClose } from '~ui/components/icons/specific/IconX'
import { Box } from '~ui/components/layout/Box'
import { Text } from '~ui/components/typography/Text/Text'
import { useTheme } from '~ui/theme/ThemeProvider'

type TProps = NoChildren & {
  /**
   * the text message presented by the toast
   */
  message: string | ((textProps: PropsOf<typeof Text>) => React.ReactNode)
  /**
   * message/toast type or severity
   */
  type: TStatusColorKey | `card`
  /**
   * callback called when the close button is clicked,
   * if not provided, the close button is not rendered
   */
  onClose?: () => void
  /**
   * custom icon to be rendered instead of the default one
   */
  customIcon?: PropsOf<typeof Icon>['icon']
}

const toastIcons: Record<TProps['type'], Nullable<PropsOf<typeof Icon>['icon']>> = {
  info: IconInfoFill,
  error: IconAlertFill,
  alert: IconAlertFill,
  success: IconCheckMarkCircleFill,
  card: null,
}

const textProps: PropsOf<typeof Text> = {
  fontStyle: `regular`,
  fontSize: `m16`,
}

const ICON_SIZE = `1.4rem`

/**
 * the component rendering message as a toast (popup on the top of the screen),
 * if you want to present a message as a toast,
 * consider using the `useToast` hook that wraps this component & handles it presentation instead
 */
export const Toast: React.FC<TProps> = ({ message, type, customIcon, onClose }) => {
  // ! Chakra toast are rendered outside of the app
  // meaning that they don’t have access to our custom provider such as theme packages’ provider
  // but our default components (BgColorProvider) and hooks (useFgColorValue) depends on it

  // after we update React & Chakra, we can solve this with custom ToastContainer (https://github.com/chakra-ui/chakra-ui/pull/5797)
  // until then, the workaround is to create custom Theme provider here & custom top-level bg color provider
  // based on Chakra theme provider that is available here

  const theme = useTheme()
  const toastIcon = customIcon || toastIcons[type]

  return (
    <ThemeProviderRaw value={theme}>
      <DefinedBgColorProvider bgColor={`bgColors.primary`}>
        <Card
          display={`grid`}
          gridTemplateColumns={toastIcon ? `auto 1fr auto` : `1fr auto`}
          columnGap={`medium`}
          alignItems={`start`}
          shadow={`bottomLong`}
          bgColor={`bgColors.${type}`}
          padding={{ horizontal: `medium`, vertical: `smallMedium` }}
        >
          {toastIcon && <Icon icon={toastIcon} iconSize={ICON_SIZE} minHeight={ICON_SIZE} />}

          <Box>
            {typeof message === 'string' ? (
              <Text {...textProps}>{message}</Text>
            ) : (
              message(textProps)
            )}
          </Box>

          {onClose && (
            <ButtonIcon
              icon={IconClose}
              iconSize={ICON_SIZE}
              padding={`none`}
              variant={`ghost`}
              onClick={onClose}
            />
          )}
        </Card>
      </DefinedBgColorProvider>
    </ThemeProviderRaw>
  )
}
